// @ts-nocheck
import { batchGetProducts } from "@insite/client-framework/Services/ProductService";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import {
    OrderUploadRowError,
    UploadedItem,
} from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import { useBunzlSettings, useWebsiteName } from "@store/Framework/Store/Context/ContextHooks";
import { useState } from "react";

interface ItemForRetry {
    item: UploadedItem;
    index: number;
}
const useOrderUpload = () => {
    const [errors, setErrors] = useState<OrderUploadRowError[]>([]);
    const [products, setProducts] = useState<ProductDto[]>([]);
    const productPrefix = useBunzlSettings()?.productPrefix;
    const validateProduct = (product: ProductDto) => {
        if (product.canConfigure || (product.isConfigured && !product.isFixedConfiguration)) {
            return UploadError.ConfigurableProduct;
        }

        if (product.isStyleProductParent) {
            return UploadError.StyledProduct;
        }

        if (!product.canAddToCart) {
            return UploadError.Unavailable;
        }

        return UploadError.None;
    };
    const getProductUnitOfMeasures = (product: ProductDto, item: UploadedItem) => {
        const lowerCaseItemUm = item.unitOfMeasure ? item.unitOfMeasure.toLowerCase() : "";

        return lowerCaseItemUm && product.productUnitOfMeasures
            ? product.productUnitOfMeasures.filter(
                  u =>
                      u.unitOfMeasure.toLowerCase() === lowerCaseItemUm ||
                      u.unitOfMeasureDisplay.toLowerCase() === lowerCaseItemUm ||
                      u.description.toLowerCase() === lowerCaseItemUm,
              )
            : [];
    };
    const setProductUnitOfMeasure = (
        product: ProductDto,
        item: UploadedItem,
        index: number,
        rowErrors: OrderUploadRowError[],
    ) => {
        const uoms = getProductUnitOfMeasures(product, item);
        if (uoms.length > 0) {
            const um = uoms[0];
            product.selectedUnitOfMeasure = um.unitOfMeasure;
            product.selectedUnitOfMeasureDisplay = um.unitOfMeasureDisplay;
            product.unitOfMeasure = um.unitOfMeasure;
            product.unitOfMeasureDisplay = um.unitOfMeasureDisplay;

            return false;
        }

        if (item.unitOfMeasure) {
            const errorProduct = mapRowError(index, UploadError.InvalidUnit, item.name, product);
            errorProduct.umRequested = item.unitOfMeasure;
            rowErrors.push(errorProduct);

            return true;
        }

        return false;
    };
    const mapRowError = (index: number, error: UploadError, name: string, product: ProductDto): OrderUploadRowError => {
        return {
            index,
            error,
            name,
            qtyRequested: product.qtyOrdered,
            umRequested: product.unitOfMeasureDisplay,
            qtyOnHand: product.qtyOnHand,
        };
    };
    const retryMissingProducts = async (
        itemsForRetry: ItemForRetry[],
        rowErrors: OrderUploadRowError[],
        products: ProductDto[],
        uploadedItems: UploadedItem[],
    ) => {
        const names = itemsForRetry.map(product => product.item.name);
        const indexes = itemsForRetry.map(product => product.index);
        const namesWithPrefix = names.map(name => `${productPrefix}${name}`);
        const productResults = await batchGetProducts({ extendedNames: namesWithPrefix });
        indexes.forEach((uploadedItemIndex, index) => {
            const item = uploadedItems[uploadedItemIndex];
            const product = productResults[index];

            if (product) {
                const error = validateProduct(product);
                if (error === UploadError.None) {
                    product.qtyOrdered = !item.qtyOrdered ? 1 : item.qtyOrdered;
                    const isInvalidUnitOfMeasure = setProductUnitOfMeasure(product, item, uploadedItemIndex, rowErrors);
                    if (!isInvalidUnitOfMeasure) {
                        products.push(product);
                    }
                } else {
                    rowErrors.push(mapRowError(uploadedItemIndex, error, item.name, product));
                }
            } else {
                rowErrors.push(
                    mapRowError(uploadedItemIndex, UploadError.NotFound, item.name, {
                        qtyOrdered: item.qtyOrdered,
                        unitOfMeasureDisplay: item.unitOfMeasure,
                    } as ProductDto),
                );
            }
        });
    };

    const uploadProducts = async (uploadedItems: UploadedItem[]) => {
        const extendedNames = uploadedItems.map(item => item.name);
        const productResults = await batchGetProducts({ extendedNames });
        const missingProducts: ItemForRetry[] = [];
        const rowErrors: OrderUploadRowError[] = [];

        productResults.forEach((product, index) => {
            const item = uploadedItems[index];
            if (product) {
                const error = validateProduct(product);

                if (error === UploadError.None) {
                    product.qtyOrdered = !item.qtyOrdered ? 1 : item.qtyOrdered;
                    const isInvalidUnitOfMeasure = setProductUnitOfMeasure(product, item, index, rowErrors);
                    if (!isInvalidUnitOfMeasure) {
                        products.push(product);
                    }
                } else {
                    rowErrors.push(mapRowError(index, error, item.name, product));
                }
            } else {
                missingProducts.push({ item, index });
            }
        });
        await retryMissingProducts(missingProducts, rowErrors, products, uploadedItems);
        setErrors(rowErrors);
        setProducts(products);
        return { rowErrors, products };
    };

    return {
        errors,
        products,
        uploadProducts,
    };
};

export default useOrderUpload;
